import { useEffect, useId, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { DATE_PLACEHOLDER, formatDisplayDate, parseToIsoDate } from '../../utils/dateFormat';
import CalendarIcon from './CalendarIcon';
import CalendarPopoverPortal from './CalendarPopoverPortal';
import MonthCalendarPanel from './MonthCalendarPanel';
import { useCalendarPopover } from './useCalendarPopover';

type FormattedDateInputProps = {
  id?: string;
  className?: string;
  /** Stored value as ISO `YYYY-MM-DD`. */
  value: string;
  onChange: (isoDate: string) => void;
  placeholder?: string;
  /** ISO `YYYY-MM-DD` — dates after this are not selectable. */
  maxDate?: string;
  /** ISO `YYYY-MM-DD` — dates before this are not selectable. */
  minDate?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  /** Calendar opens year grid → month → day (easier for DOB-style dates). */
  enableYmdViews?: boolean;
};

/** Blocks keyboard typing while keeping Tab / arrow navigation usable. */
function blockManualDateEntry(e: React.KeyboardEvent<HTMLInputElement>) {
  if (e.key === 'Tab' || e.key.startsWith('Arrow') || e.key === 'Escape') return;
  if (e.key === 'Enter' || e.key === ' ') return;
  if (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete') {
    e.preventDefault();
  }
}

export default function FormattedDateInput({
  id,
  className = 'ccr-inp',
  value,
  onChange,
  placeholder = DATE_PLACEHOLDER,
  maxDate,
  minDate,
  disabled,
  style,
  enableYmdViews = true,
}: FormattedDateInputProps) {
  const popoverId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const valueIso = value ? parseToIsoDate(value) : '';
  const displayValue = valueIso ? formatDisplayDate(valueIso) : '';
  const [viewMonth, setViewMonth] = useState(() =>
    valueIso ? dayjs(valueIso).startOf('month') : dayjs().startOf('month'),
  );

  useEffect(() => {
    if (!valueIso) return;
    setViewMonth(dayjs(valueIso).startOf('month'));
  }, [valueIso]);

  useCalendarPopover(open, wrapRef, popoverRef, () => setOpen(false));

  const openCalendar = () => {
    if (disabled) return;
    setOpen(true);
  };

  const pickDay = (iso: string) => {
    onChange(iso);
    setOpen(false);
  };

  const inputClass = `${className} cal-field-inp`;

  return (
    <div className="cal-field" ref={wrapRef}>
      <input
        id={id}
        className={inputClass}
        type="text"
        readOnly
        autoComplete="off"
        disabled={disabled}
        style={style}
        placeholder={placeholder}
        value={displayValue}
        onClick={openCalendar}
        onFocus={openCalendar}
        onPaste={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          blockManualDateEntry(e);
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openCalendar();
          }
        }}
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
      />
      <button
        type="button"
        className="cal-field-btn"
        disabled={disabled}
        aria-label="Open calendar"
        aria-expanded={open}
        aria-controls={popoverId}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
      >
        <CalendarIcon />
      </button>

      <CalendarPopoverPortal
        open={open}
        anchorRef={wrapRef}
        popoverRef={popoverRef}
        id={popoverId}
        className="cal-popover cal-popover--single"
        ariaLabel="Select date"
        estimatedHeight={enableYmdViews ? 340 : 300}
      >
        <MonthCalendarPanel
          month={viewMonth}
          onMonthChange={setViewMonth}
          selected={valueIso}
          onDaySelect={pickDay}
          minDate={minDate}
          maxDate={maxDate}
          enableYmdViews={enableYmdViews}
        />
      </CalendarPopoverPortal>
    </div>
  );
}
